import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { cartService, orderService } from '../services/api';
import SiteLoading from '../components/SiteLoading';
import useSiteBranding from '../hooks/useSiteBranding';
import './OrderSuccessPage.css';

const OrderSuccessPage = () => {
  const { siteName, siteLogoUrl } = useSiteBranding();
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState('loading'); // loading | success | error
  const [orderData, setOrderData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!orderId) {
      setStatus('error');
      setErrorMessage('Order not found.');
      return;
    }

    const loadOrder = async () => {
      try {
        const res = await orderService.getOrder(orderId);
        if (res?.success && res?.data) {
          setOrderData(res.data);
          setStatus('success');
          try {
            await cartService.clearCart();
          } catch (e) {
            console.warn('Cart clear:', e);
          }
        } else {
          setStatus('error');
          setErrorMessage(res?.message || 'Could not load this order.');
        }
      } catch (err) {
        console.error('Order load error:', err);
        setStatus('error');
        setErrorMessage(err?.message || 'Could not load this order.');
      }
    };

    loadOrder();
  }, [orderId]);

  if (status === 'loading') {
    return (
      <div className="order-success-page">
        <div className="order-success-container">
          <SiteLoading
            title="Loading your order"
            subtitle="Please wait..."
            icon="📦"
            variant="full"
          />
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="order-success-page">
        <div className="order-success-container">
          <div className="order-success-card error">
            <div className="error-icon">
              <i className="fas fa-exclamation-circle"></i>
            </div>
            <h1>Unable to show order</h1>
            <p>{errorMessage}</p>
            <div className="order-success-actions">
              <button className="btn-primary" onClick={() => navigate('/orders')}>
                <i className="fas fa-list"></i> View Orders
              </button>
              <button className="btn-secondary" onClick={() => navigate('/')}>
                <i className="fas fa-home"></i> Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const order = orderData;
  const orderNumber = order?.orderNumber || order?.orderId || orderId || 'N/A';
  const total = order?.total ?? 0;
  const deliveryDate = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toLocaleDateString();

  return (
    <div className="order-success-page">
      <div className="order-success-container">
        <div className="order-success-card">
          {siteLogoUrl && (
            <div className="order-success-brand">
              <img src={siteLogoUrl} alt={siteName} className="order-success-logo" />
            </div>
          )}
          <div className="success-animation">
            <div className="checkmark-circle">
              <div className="checkmark"></div>
            </div>
          </div>
          <h1 className="success-title">
            <i className="fas fa-check-circle"></i>
            Order confirmed
          </h1>
          <p className="success-subtitle">
            Thank you. Your order has been placed successfully.
          </p>

          <div className="order-summary-box">
            <div className="order-summary-row">
              <span>Order Number</span>
              <strong>#{orderNumber}</strong>
            </div>
            <div className="order-summary-row">
              <span>Total</span>
              <strong>Rs. {Number(total).toLocaleString()}</strong>
            </div>
            <div className="order-summary-row">
              <span>Expected Delivery</span>
              <strong>{deliveryDate}</strong>
            </div>
          </div>

          <div className="order-success-actions">
            <button className="btn-primary" onClick={() => navigate('/orders')}>
              <i className="fas fa-truck"></i> Track Your Order
            </button>
            <button className="btn-secondary" onClick={() => navigate('/')}>
              <i className="fas fa-shopping-bag"></i> Continue Shopping
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccessPage;
